import { useEffect, useState } from 'react';
import { Panel } from '@dayon/design-system';
import { formatCount, formatPesos } from '@dayon/money';
import type { PageMeta, WithdrawalRow } from '@dayon/contracts';
import { api } from '../../lib/api.ts';
import { datedStamp, payoutLabel, stageWords } from './payout-state.ts';

/**
 * THIS PLAYER'S OTHER REQUESTS — the review drawer's list of the player's other requests, the ten
 * newest, and how each one ended.
 *
 * The drawer used to say nothing about the player's history, because nothing here could fetch it:
 * a checker had the lifetime payout ratio and no requests behind it. The queue read now takes a
 * player (`playerId`) and answers that player's requests newest first, whatever their status or
 * park, inside the reader's brands. This section reads the ten newest when the drawer opens and
 * lists every one of them but the request the drawer is open on.
 *
 * THE NAME SAYS WHAT THE LIST HOLDS. It was called "Prior payouts for this player", and that made
 * two claims the list never kept: that the requests came before the open one — the read gives the
 * player's newest ten, so a request asked for after it is listed too — and that they were payouts —
 * a request still waiting for a checker, or held for risk, has paid nothing. The walk of 13 October
 * opened a request dated 7 July and found two dated 8 and 9 July under it; it opened another and
 * found both of the requests under it "Held by risk". The list was right and its name was wrong,
 * so the name changed and the list did not: every other request, newest first, dated, each with its
 * amount and how it ended.
 *
 * THE WORDS ARE THE SCREEN'S OWN, imported rather than restated: the stage the queue's Stage column
 * prints (`stageWords`) and where a released payout stands as its Payout column prints it
 * (`payoutLabel`), both from payout-state.ts, which imports nothing from the screen — so this part
 * of the drawer and the screen that mounts it form no import cycle. Plain text rather than tags, so
 * the drawer still holds one stage tag — the open request's. Dates carry the year, because a
 * player's history runs back further than a year can be taken for granted.
 *
 * A failed read says so in one quiet line and never reads as "no other requests"; while the read
 * is on its way the section says that, and nothing about how many there are.
 */

/** How many of the player's newest requests the drawer asks for — the open one among them, or not. */
const OTHER_REQUESTS_PAGE = 10;

/** What the drawer knows of the player's requests: still reading, read, or not read. */
export type OtherRequestsRead =
  | { state: 'reading' }
  | { state: 'failed' }
  | { state: 'read'; rows: WithdrawalRow[]; total: number };

/** The player's newest requests, in one read. A read that fails is a failure, never an empty list. */
export async function readOtherRequests(playerId: string): Promise<OtherRequestsRead> {
  try {
    const page = await api.get<{ items: WithdrawalRow[]; meta: PageMeta }>(
      `/api/withdrawals?${new URLSearchParams({ playerId, perPage: String(OTHER_REQUESTS_PAGE) })}`,
    );
    return { state: 'read', rows: page.items, total: page.meta.total };
  } catch {
    return { state: 'failed' };
  }
}

/** How a request ended, in the queue's words: its stage and, once released, where the payout stands. */
const outcomeOf = (w: WithdrawalRow): string => {
  const payout = payoutLabel(w);
  return payout === null ? stageWords(w) : `${stageWords(w)} · ${payout}`;
};

/** The section, drawn from a read: the player's requests but the open one, newest first. */
export function OtherRequestsList({ read, openRef }: { read: OtherRequestsRead; openRef: string }) {
  if (read.state !== 'read') {
    return (
      <Panel title="This player's other requests">
        <div className="text-subtle">
          {read.state === 'reading'
            ? "Reading the player's other requests…"
            : "The player's other requests could not be read."}
        </div>
      </Panel>
    );
  }
  const others = read.rows.filter((w) => w.ref !== openRef);
  /* Every request the player has in the reader's brands but the open one, which is always among
     them — so the drawer can say when it lists fewer than there are. */
  const allOthers = Math.max(read.total - 1, others.length);
  return (
    <Panel title="This player's other requests">
      {others.length === 0 ? (
        <div className="text-subtle">No other requests from this player.</div>
      ) : (
        <div className="stack">
          <dl className="stack" style={{ margin: 0 }}>
            {others.map((w) => (
              <div className="def-row" key={w.id}>
                <dt>
                  <span className="tabular">{w.ref}</span>
                  <div className="text-subtle" style={{ fontSize: '11px' }}>
                    {datedStamp.format(new Date(w.requestedAt))}
                  </div>
                </dt>
                <dd>
                  <span className="tabular">{formatPesos(BigInt(w.amount))}</span>
                  <div className="text-subtle" style={{ fontSize: '11px' }}>
                    {outcomeOf(w)}
                  </div>
                </dd>
              </div>
            ))}
          </dl>
          {others.length < allOthers ? (
            <div className="text-subtle" style={{ fontSize: '11px' }}>
              {`The ${formatCount(others.length)} most recent of ${formatCount(allOthers)} are listed.`}
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

/** The drawer's section: reads the player's requests when the drawer opens on one of them. */
export function OtherRequests({ row }: { row: Pick<WithdrawalRow, 'playerId' | 'ref'> }) {
  const [read, setRead] = useState<OtherRequestsRead>({ state: 'reading' });
  useEffect(() => {
    /* Read again for every request the drawer opens on; a read that lands after it moved on changes
       nothing. */
    let current = true;
    setRead({ state: 'reading' });
    void readOtherRequests(row.playerId).then((result) => {
      if (current) setRead(result);
    });
    return () => {
      current = false;
    };
  }, [row.playerId, row.ref]);
  return <OtherRequestsList read={read} openRef={row.ref} />;
}
